// src/components/PaymentModal.jsx
import React, { useState, useEffect } from "react";
import "../style/ModalDelete.css";
import "../style/PaymentModal.css";
import Locations from '@pixelpay/sdk-core/lib/resources/Locations';

// Detect card brand based on number
const detectCardBrand = (number) => {
  const cleaned = number.replace(/\D/g, "");
  if (/^4/.test(cleaned)) return "visa";
  if (/^(5[1-5]|2[2-7])/.test(cleaned)) return "mastercard";
  return null;
};

function PaymentModal({ show, onClose, onConfirm, message, amount, orderAmount = 1, paymentsEnabled = false }) {
  const [formData, setFormData] = useState({
    customer_name: "",
    card_holder: "",
    card_number: "",
    card_expire: "",
    card_cvv: "",
    customer_email: "",
    billing_phone: "",
    billing_country: "",
    billing_state: "",
    billing_city: "",
    billing_address: ""
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [stateOptions, setStateOptions] = useState([{ value: "", label: "Selecciona un estado" }]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const cardBrand = detectCardBrand(formData.card_number);

  const rawCountries = Locations.countriesList();
  const baseCountryOption = { value: "", label: "Selecciona un país" };
  let countryOptions = [baseCountryOption];
  if (Array.isArray(rawCountries)) {
    countryOptions = countryOptions.concat(
      rawCountries.map(c => ({
        value: c.code || c.value || c.iso2 || "",
        label: c.name || c.label || c.iso2 || c.code || "",
      }))
    );
  } else if (rawCountries && typeof rawCountries === 'object') {
    countryOptions = countryOptions.concat(
      Object.entries(rawCountries).map(([code, country]) => ({
        value: code,
        label: typeof country === 'object'
          ? country.name || country.label || country.iso2 || code
          : String(country)
      }))
    );
  }

  // Update state options when country changes
  useEffect(() => {
    const baseStateOption = { value: "", label: "Selecciona un estado" };
    if (formData.billing_country) {
      const rawStates = Locations.statesList(formData.billing_country);
      let opts = [baseStateOption];
      if (Array.isArray(rawStates)) {
        opts = opts.concat(
          rawStates.map(s => ({
            value: s.code || s.value || s.iso2 || "",
            label: s.name || s.label || s.iso2 || s.code || "",
          }))
        );
      } else if (rawStates && typeof rawStates === 'object') {
        opts = opts.concat(
          Object.entries(rawStates).map(([code, state]) => ({
            value: code,
            label: typeof state === 'object'
              ? state.name || state.label || state.iso2 || code
              : String(state)
          }))
        );
      }
      setStateOptions(opts);
    } else {
      setStateOptions([baseStateOption]);
    }
    // Clear previous selection
    setFormData(prev => ({ ...prev, billing_state: "" }));
  }, [formData.billing_country]);

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.customer_name.trim()) errs.customer_name = "Nombre requerido";
    if (!formData.card_holder.trim()) errs.card_holder = "Nombre en tarjeta requerido";
    const cleaned = formData.card_number.replace(/\s/g, "");
    if (!/^\d{13,19}$/.test(cleaned)) errs.card_number = "Número de tarjeta inválido";
    if (!/^(0[1-9]|1[0-2])\/[0-9]{2}$/.test(formData.card_expire)) errs.card_expire = "Formato MM/AA inválido";
    if (!/^\d{3,4}$/.test(formData.card_cvv)) errs.card_cvv = "CVV inválido";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) errs.customer_email = "Email inválido";
    if (!formData.billing_phone) errs.billing_phone = "Telefono requerido";
    if (!formData.billing_country) errs.billing_country = "País requerido";
    if (!formData.billing_state) errs.billing_state = "Estado requerido";
    if (!formData.billing_city) errs.billing_city = "Ciudad requerida";
    if (!formData.billing_address) errs.billing_address = "Dirección requerida";
    if (!termsAccepted) errs.termsAccepted = "Debes aceptar los términos y condiciones.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === "card_number") formatted = value.replace(/\D/g, "").match(/.{1,4}/g)?.join(" ") || value;
    if (name === "card_expire") {
      const digits = value.replace(/\D/g, "");
      formatted = digits.length > 2
        ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}`
        : digits;
    }
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleTermsChange = ({ target: { checked } }) => {
    setTermsAccepted(checked);
    setErrors(prev => {
      if (!prev.termsAccepted) return prev;
      if (checked) {
        const { termsAccepted: _ignored, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  const handleConfirm = async () => {
    if (!paymentsEnabled) {
      return;
    }
    if (validate()) {
      setIsProcessing(true);
      try {
        const finalAmount = 1;
        await onConfirm({ ...formData, order_amount: finalAmount.toFixed(2), termsAccepted: true });
      } catch (err) {
        console.error("Error confirming payment", err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const visaURL = "https://res.cloudinary.com/dlpsbcfs4/image/upload/v1753489411/visa_sg3o2a.png";
  const mcURL = "https://res.cloudinary.com/dlpsbcfs4/image/upload/v1753489374/Mastercard_pq5vgz.png";

  if (!paymentsEnabled) {
    if (!show) {
      return null;
    }
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content payment-modal disabled" onClick={e => e.stopPropagation()}>
          <button className="close-button-left" onClick={onClose}>X</button>
          <h2>Pagos deshabilitados</h2>
          <p>Los pagos en línea están temporalmente deshabilitados. Por favor, contáctanos para completar tu solicitud.</p>
        </div>
      </div>
    );
  }

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payment-modal" onClick={e => e.stopPropagation()}>
        {amount && <p className="auction-ticket">Monto a pagar: {amount}</p>}
        <button className="close-button-left" onClick={onClose}>X</button>
        <h2>Datos de pago</h2>
        {message && <p style={{ marginBottom: "1rem" }}>{message}</p>}
        <div className="payment-form">
          {/* Name */}
          <div className="form-group">
            <input name="customer_name" type="text" placeholder="Nombre completo" disabled={isProcessing}
              className={errors.customer_name ? "error-input" : ""}
              value={formData.customer_name} onChange={handleChange} />
            {errors.customer_name && <small className="error">{errors.customer_name}</small>}
          </div>

          <div className="form-group">
            <input name="card_holder" type="text" placeholder="Nombre en la tarjeta" disabled={isProcessing}
              className={errors.card_holder ? "error-input" : ""}
              value={formData.card_holder} onChange={handleChange} />
            {errors.card_holder && <small className="error">{errors.card_holder}</small>}
          </div>

          {/* Card Number */}
          <div className="form-group card-number-group">
            <label className="labels">Número de tarjeta</label>
            <input name="card_number" type="text" placeholder="1234 5678 9012 3456" disabled={isProcessing} maxLength={19}
              className={errors.card_number ? "error-input" : ""}
              value={formData.card_number} onChange={handleChange} />
            {cardBrand && <img src={cardBrand === 'visa' ? visaURL : mcURL} alt={cardBrand} />}
            {errors.card_number && <small className="error">{errors.card_number}</small>}
          </div>

          {/* Expiry & CVV */}
          <div className="row">
            <div className="form-group half">
              <label className="labels">Expiración</label>
              <input name="card_expire" type="text" placeholder="MM/AA" disabled={isProcessing} maxLength={5}
                className={errors.card_expire ? "error-input" : ""}
                value={formData.card_expire} onChange={handleChange} />
              {errors.card_expire && <small className="error">{errors.card_expire}</small>}
            </div>
            <div className="form-group half">
              <label className="labels">CCV</label>
              <input name="card_cvv" type="password" placeholder="123" disabled={isProcessing} maxLength={4}
                className={errors.card_cvv ? "error-input" : ""}
                value={formData.card_cvv} onChange={handleChange} />
              {errors.card_cvv && <small className="error">{errors.card_cvv}</small>}
            </div>
          </div>

          {/* Billing Info */}
          <label>Información de facturación</label>
          <div className="form-group">
            <input name="customer_email" type="email" placeholder="Correo electrónico" disabled={isProcessing}
              className={errors.customer_email ? "error-input" : ""}
              value={formData.customer_email} onChange={handleChange} />
            {errors.customer_email && <small className="error">{errors.customer_email}</small>}
          </div>

          <div className="form-group">
            <input name="billing_phone" type="phone" placeholder="Numero de telefono" disabled={isProcessing}
              className={errors.billing_phone ? "error-input" : ""}
              value={formData.billing_phone} onChange={handleChange} />
            {errors.billing_phone && <small className="error">{errors.billing_phone}</small>}
          </div>

          {/* Country & State */}
          <div className="row">
            <div className="form-group half">
              <select name="billing_country" disabled={isProcessing}
                className={errors.billing_country ? "error-input" : ""}
                value={formData.billing_country} onChange={handleChange}>
                {countryOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {errors.billing_country && <small className="error">{errors.billing_country}</small>}
            </div>
            <div className="form-group half">
              <select name="billing_state" disabled={isProcessing || !formData.billing_country}
                className={errors.billing_state ? "error-input" : ""}
                value={formData.billing_state} onChange={handleChange}>
                {stateOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {errors.billing_state && <small className="error">{errors.billing_state}</small>}
            </div>
          </div>

          <div className="form-group">
            <input name="billing_city" type="text" placeholder="Ciudad" disabled={isProcessing}
              className={errors.billing_city ? "error-input" : ""}
              value={formData.billing_city} onChange={handleChange} />
            {errors.billing_city && <small className="error">{errors.billing_city}</small>}
          </div>
          <div className="form-group">
            <input name="billing_address" type="text" placeholder="Dirección" disabled={isProcessing}
              className={errors.billing_address ? "error-input" : ""}
              value={formData.billing_address} onChange={handleChange} />
            {errors.billing_address && <small className="error">{errors.billing_address}</small>}
          </div>

        </div>

        <div className="terms-section">
          <h3>Términos y condiciones empresariales</h3>
          <div className="terms-content">
            <p><strong>1. Autorización de pago.</strong> Al proporcionar los datos de la tarjeta usted declara que está
              autorizado para realizar pagos en nombre de la empresa y que tiene la facultad de obligarla frente a
              Honduautos. Todo pago es definitivo una vez aprobado por el emisor.</p>
            <p><strong>2. Procesamiento seguro.</strong> Honduautos procesa los pagos mediante proveedores certificados y
              puede ejecutar verificaciones antifraude adicionales. La operación podrá ser rechazada si se detecta
              actividad sospechosa.</p>
            <p><strong>3. Obligaciones de facturación.</strong> Usted acepta proporcionar información de facturación veraz y
              completa. Las facturas se emitirán con base en los datos ingresados y cualquier corrección posterior
              podría generar cargos administrativos.</p>
            <p><strong>4. Políticas de reembolso.</strong> Los reembolsos o ajustes están sujetos a los acuerdos comerciales
              vigentes con Honduautos y a la normativa aplicable. Las solicitudes deberán presentarse por escrito dentro
              de los plazos establecidos contractualmente.</p>
            <p><strong>5. Uso de datos.</strong> La información suministrada se utilizará únicamente para gestionar la
              transacción y cumplir con obligaciones legales de prevención de fraude y contabilidad. Para más detalles
              consulte nuestro Aviso de Privacidad.</p>
            <p><strong>6. Jurisdicción.</strong> Toda controversia relacionada con este pago se regirá por las leyes de
              Honduras y cualquier disputa se someterá a los tribunales competentes de Tegucigalpa.</p>
          </div>
          <label className="terms-checkbox">
            <input type="checkbox" checked={termsAccepted} onChange={handleTermsChange} disabled={isProcessing} />
            <span>He leído y acepto los términos y condiciones de pago empresarial.</span>
          </label>
          {errors.termsAccepted && <small className="error">{errors.termsAccepted}</small>}
        </div>

        <p className="payment-note">{message}</p>
        <div className="modal-confirm">
          <button className="red-button" disabled={isProcessing} onClick={handleConfirm}>
            {isProcessing ? "Procesando..." : "Confirmar"}
          </button>
        </div>

        <div className="trust-logos">
          <img className="logosPIXELSSL" src="https://res.cloudinary.com/dlpsbcfs4/image/upload/v1753489345/ssl_vxg6xw.jpg" alt="SSL Secure" />
          <img className="logosPIXELSSL" src="https://res.cloudinary.com/dlpsbcfs4/image/upload/v1753489304/pixelpay_dgo9z7.png" alt="Provider Logo" />
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
